import {
  CLOUD_LEVELS,
  COMBAT,
  MEGASTRUCTURE,
  SHIP_CLASSES,
  SHIP_CLASS_IDS,
  SHIP_COMBAT,
  STANCE_IDS,
  type ShipClassId,
  type StanceId,
} from '@sd/content';
import { t, type Locale } from '@sd/i18n';
import {
  liveCloudAt,
  commandBlocker,
  fleetLimit,
  fleetRetreatAt,
  fleetStance,
  fleetUpkeep,
  hexKey,
  hullShare,
  isArmed,
  leadShip,
  parallelBuilds,
  shipsOf,
  techMods,
  toLocal,
  type Command,
  type FleetState,
  type GameState,
} from '@sd/sim';
import { TICKS_PER_SECOND } from '../game/protocol';
import { describeHex, shipSummary, type Viewer } from '../map/describe';
import { clock, fleetName, num, shortOf } from './format';
import { reasonText } from './planetModel';

/**
 * The «Fleet» tab (#58): the spaceport with its queue and what it can lay
 * down, and every fleet the player owns with the orders that make sense
 * where it stands. Whether an order would go through is asked of the
 * simulation itself (`commandBlocker`), so a greyed button gives its reason.
 */
export interface ShipOption {
  readonly id: ShipClassId;
  readonly name: string;
  readonly stats: string;
  readonly cash: number;
  readonly alloys: number;
  readonly time: string;
  readonly command: Command | null;
  readonly reason: string | null;
}

export interface FleetAction {
  readonly id: string;
  readonly label: string;
  readonly command: Command | null;
  readonly reason: string | null;
  readonly primary?: boolean;
  readonly heat?: boolean;
}

export interface FleetRow {
  readonly id: number;
  readonly title: string;
  /** The class it goes by: its icon on the units bar (#154). */
  readonly lead: ShipClassId;
  readonly armed: boolean;
  /** Holding, scanning nothing, carrying nothing: waiting for an order. */
  readonly idle: boolean;
  /** One ship: its name says what it is, no list of classes beside it. */
  readonly single: boolean;
  readonly where: string;
  readonly motion: string;
  readonly ships: string;
  readonly hull: number;
  readonly stance: StanceId;
  readonly stances: readonly {
    readonly id: StanceId;
    readonly label: string;
    readonly command: Command | null;
  }[];
  readonly retreat: string;
  readonly actions: readonly FleetAction[];
  /** Ways to split it: one per ship class, when something would stay behind. */
  readonly splits: readonly FleetAction[];
  readonly merges: readonly FleetAction[];
  readonly busy: string | null;
  /** It can carry out an order right where it stands: scan, build, raid, land, give (#152). */
  readonly ready: boolean;
}

export interface FleetView {
  readonly ships: number;
  readonly limit: number;
  readonly upkeep: { readonly cash: number; readonly alloys: number; readonly energy: number };
  readonly parallel: number;
  readonly queue: readonly {
    readonly name: string;
    readonly left: string;
    readonly building: boolean;
    /** Share built, 0..1 (#115: a bar at the top of the tab). */
    readonly value: number;
  }[];
  readonly options: readonly ShipOption[];
  readonly fleets: readonly FleetRow[];
}

/** «корпус 300 · урон 12 · 4 с/гекс · трюм 40 · силён против: Корвет». */
export function shipStats(locale: Locale, id: ShipClassId): string {
  const c = SHIP_COMBAT[id];
  const def = SHIP_CLASSES[id];
  const parts = [
    t(locale, 'fleet.stat.hull', { n: c.hull }),
    c.damage ? t(locale, 'fleet.stat.damage', { n: c.damage }) : t(locale, 'fleet.stat.unarmed'),
    t(locale, 'fleet.stat.speed', { s: num(locale, def.ticksPerHex / TICKS_PER_SECOND, 1) }),
  ];
  if (def.cargo) parts.push(t(locale, 'fleet.stat.cargo', { n: def.cargo }));
  if (c.strongAgainst.length) {
    parts.push(
      t(locale, 'fleet.stat.strong', {
        list: c.strongAgainst.map((x) => t(locale, `ship.${x}.name`)).join(', '),
      }),
    );
  }
  return parts.join(' · ');
}

const reason = (locale: Locale, code: string | null, tech?: string) =>
  code ? reasonText(locale, code, tech, 'fleet') : null;

export function fleetView(state: GameState, v: Viewer): FleetView | null {
  const { locale, me } = v;
  const player = state.players.find((p) => p.id === me);
  if (!player) return null;
  const mods = techMods(player.techs);
  const ships = shipsOf(state.fleets, me);
  const limit = fleetLimit(player.planet) + mods.fleetLimit;
  const raw = fleetUpkeep(ships, limit);
  const parallel = parallelBuilds(player.planet);

  const options = SHIP_CLASS_IDS.filter((id) => {
    const f = SHIP_CLASSES[id].faction;
    return f === null || f === player.faction;
  }).map((id): ShipOption => {
    const def = SHIP_CLASSES[id];
    const command: Command = { type: 'buildShip', playerId: me, ship: id };
    const blocker = commandBlocker(state, command);
    const cash = toLocal(def.cost.cash, player.currency.rate);
    const missing =
      blocker && /not enough/.test(blocker) ? shortOf(locale, player, cash, def.cost.alloys) : null;
    return {
      id,
      name: t(locale, `ship.${id}.name`),
      stats: shipStats(locale, id),
      cash,
      alloys: def.cost.alloys,
      time: clock(def.buildTicks),
      command: blocker ? null : command,
      reason: missing ?? reason(locale, blocker, def.requiresTech ?? undefined),
    };
  });

  const queue = player.shipyard.map((o, i) => ({
    name: t(locale, `ship.${o.ship}.name`),
    left: clock(o.ticksLeft),
    building: i < parallel,
    value: i < parallel ? 1 - o.ticksLeft / SHIP_CLASSES[o.ship].buildTicks : 0,
  }));

  const mine = state.fleets.filter((f) => f.owner === me).sort((a, b) => a.id - b.id);
  const fleets = mine.map((f) => fleetRow(state, f, mine, v));

  return {
    ships: ships.length,
    limit,
    upkeep: {
      cash: toLocal(raw.cash * mods.fleetUpkeep, player.currency.rate),
      alloys: raw.alloys * mods.fleetUpkeep,
      energy: raw.energy * mods.fleetUpkeep,
    },
    parallel,
    queue,
    options,
    fleets,
  };
}

function fleetRow(
  state: GameState,
  fleet: FleetState,
  mine: readonly FleetState[],
  v: Viewer,
): FleetRow {
  const { locale, me } = v;
  const base = { playerId: me, fleetId: fleet.id } as const;
  const ask = (command: Command) => commandBlocker(state, command);
  const stance = fleetStance(fleet);
  const key = hexKey(fleet.hex);
  const capital = state.system.planets.find((p) => p.homeOf === me)?.hex;
  const atHome = capital !== undefined && hexKey(capital) === key;
  const has = (cls: ShipClassId) => fleet.ships.some((s) => s.cls === cls);
  const caravan = state.contracts.find((c) => c.caravan?.fleetId === fleet.id);
  const raiding = state.raids.some((r) => r.fleetId === fleet.id);

  const actions: FleetAction[] = [];
  /**
   * An order is listed when it is possible, or when the fleet stands where
   * it would make sense and only the rules stand in the way: then its button
   * is greyed with the reason. `pointless` reasons hide it altogether.
   */
  const offer = (
    id: string,
    command: Command,
    pointless: readonly string[],
    extra: Partial<FleetAction> = {},
    tech?: string,
  ) => {
    const blocker = ask(command);
    if (blocker && pointless.includes(blocker)) return;
    actions.push({
      id,
      label: t(locale, `fleet.order.${id}`),
      command: blocker ? null : command,
      reason: reason(locale, blocker, tech),
      ...extra,
    });
  };
  if (fleet.path.length > 0) offer('stop', { type: 'moveFleet', ...base, to: fleet.hex }, []);
  if (capital && !atHome)
    offer('home', { type: 'moveFleet', ...base, to: capital }, ['unreachable']);
  if (has('explorer') && !fleet.scan) {
    // A deeper cloud waits for a better scanner: name it.
    const cloud = liveCloudAt(state.system, state.exploration.findsLeft, fleet.hex);
    offer(
      'scan',
      { type: 'scan', ...base },
      ['not in a dust cloud', 'cloud exhausted', 'moving'],
      { primary: true },
      cloud ? (CLOUD_LEVELS[cloud.level].requiresTech ?? undefined) : undefined,
    );
  }
  if (has('transport'))
    offer('outpost', { type: 'buildOutpost', ...base }, ['no site here', 'moving'], {
      primary: true,
    });
  if (fleet.ships.some((s) => SHIP_COMBAT[s.cls].damage > 0) && !raiding)
    offer('raid', { type: 'raid', ...base }, ['nothing to raid', 'moving'], { heat: true });
  if (has('lander'))
    offer(
      'invade',
      { type: 'invade', ...base },
      ['not at an enemy capital', 'requires technology'],
      {
        heat: true,
      },
    );
  if (has('transport') && techMods(state.players.find((p) => p.id === me)!.techs).megastructure) {
    const command: Command = { type: 'buildMegastructure', ...base };
    const blocker = ask(command);
    if (blocker !== 'already building') {
      actions.push({
        id: 'swarm',
        label: t(locale, 'fleet.order.swarm'),
        command: blocker ? null : command,
        reason:
          blocker === 'not enough goods'
            ? t(locale, 'fleet.swarmCost', MEGASTRUCTURE.stageCost)
            : reason(locale, blocker),
        primary: true,
      });
    }
  }
  for (const project of state.projects) {
    if (hexKey(project.hex) !== key) continue;
    offer(
      'give',
      {
        type: 'contributeProject',
        playerId: me,
        projectId: project.id,
        what: 'fleet',
        fleetId: fleet.id,
      },
      [],
    );
  }

  const counts = new Map<ShipClassId, number[]>();
  for (const s of fleet.ships) counts.set(s.cls, [...(counts.get(s.cls) ?? []), s.id]);
  const splits: FleetAction[] = [];
  if (fleet.ships.length > 1) {
    for (const [cls, ids] of counts) {
      // Everything of one class would leave nothing behind: split it in half instead.
      const moving =
        ids.length === fleet.ships.length ? ids.slice(0, Math.floor(ids.length / 2)) : ids;
      if (!moving.length) continue;
      splits.push({
        id: `split-${cls}`,
        label: t(locale, 'fleet.split', {
          ships: shipSummary(
            locale,
            moving.map(() => cls),
          ),
        }),
        command: { type: 'splitFleet', ...base, shipIds: moving },
        reason: null,
      });
    }
  }
  // Fleets come with «Логистика флота», four ships at most (#160): the sim says why not.
  const merge = (o: FleetState): FleetAction => {
    const command: Command = { type: 'mergeFleets', playerId: me, fleetIds: [fleet.id, o.id] };
    const blocker = ask(command);
    return {
      id: `merge-${o.id}`,
      label: t(locale, 'fleet.merge', { name: fleetName(locale, o) }),
      command: blocker ? null : command,
      reason: blocker ? reason(locale, blocker) : null,
    };
  };
  const merges = mine.filter((o) => o.id !== fleet.id && hexKey(o.hex) === key).map(merge);

  const armed = isArmed(fleet);
  const motion = fleet.retreating
    ? t(locale, 'fleet.retreating')
    : fleet.scan
      ? t(locale, 'ui.map.scanning')
      : fleet.path.length > 0
        ? t(locale, 'ui.map.moving', { hexes: fleet.path.length })
        : t(locale, 'ui.map.holding');

  return {
    id: fleet.id,
    title: fleetName(locale, fleet),
    lead: leadShip(fleet).cls,
    single: fleet.ships.length === 1,
    armed,
    // Nothing to do and nowhere to go (#154): the units bar lights it up.
    idle: !fleet.path.length && !fleet.scan && !fleet.retreating && !caravan && !raiding,
    where: atHome ? t(locale, 'fleet.atHome') : describeHex(state, fleet.hex, v).title,
    motion,
    ships: shipSummary(
      locale,
      fleet.ships.map((s) => s.cls),
    ),
    hull: Math.round(hullShare(fleet) * 100),
    stance,
    // Unarmed ships have no stance (#154): they do not fight, and do not run.
    stances: (armed ? STANCE_IDS : []).map((id) => ({
      id,
      label: t(locale, `stance.${id}.name`),
      command: id === stance ? null : { type: 'setStance', ...base, stance: id },
    })),
    retreat: armed
      ? t(locale, 'fleet.retreatAt', {
          pct: Math.round(fleetRetreatAt(fleet) * 100),
          dealt: Math.round(COMBAT.stances[stance].dealt * 100),
          taken: Math.round(COMBAT.stances[stance].taken * 100),
        })
      : t(locale, 'fleet.unarmedHolds'),
    actions,
    splits,
    merges,
    busy: caravan
      ? t(locale, 'fleet.busy.caravan', { id: caravan.id })
      : raiding
        ? t(locale, 'fleet.busy.raid')
        : null,
    ready: actions.some((a) => a.command !== null && (a.primary || a.heat || a.id === 'give')),
  };
}
